import { z } from 'zod';
import { CoordinatesSchema, PriceSchema } from './base';
// Google Places API Response Schema
const GooglePlacesResponseSchema = z.object({
  places: z.array(
    z.object({
      id: z.string().optional().default(''),
      displayName: z
        .object({
          text: z.string(),
          languageCode: z.string().optional().default('en'),
        })
        .optional()
        .nullable(),
      formattedAddress: z.string().optional().default(''),
      location: z
        .object({
          latitude: z.number(),
          longitude: z.number(),
        })
        .optional()
        .nullable(),
      rating: z.number().optional().nullable(),
      googleMapsUri: z.string().optional().default(''),
      websiteUri: z.string().optional().default(''),
      regularOpeningHours: z
        .object({
          openNow: z.boolean().optional().default(false),
          periods: z
            .array(
              z.object({
                open: z.object({
                  day: z.number(),
                  hour: z.number(),
                  minute: z.number(),
                }),
                close: z
                  .object({
                    day: z.number(),
                    hour: z.number(),
                    minute: z.number(),
                  })
                  .optional(),
              })
            )
            .optional(),
          weekdayDescriptions: z.array(z.string()).optional().default([]),
        })
        .optional()
        .default({
          openNow: false,
          periods: [],
          weekdayDescriptions: [],
        }),
      priceLevel: z
        .enum([
          'PRICE_LEVEL_FREE',
          'PRICE_LEVEL_INEXPENSIVE',
          'PRICE_LEVEL_MODERATE',
          'PRICE_LEVEL_EXPENSIVE',
          'PRICE_LEVEL_VERY_EXPENSIVE',
        ])
        .optional(),
      userRatingCount: z.number().optional().nullable(),
      photos: z
        .array(
          z.object({
            name: z.string(),
            widthPx: z.number(),
            heightPx: z.number(),
            authorAttributions: z
              .array(
                z.object({
                  displayName: z.string(),
                  uri: z.string().optional(),
                  photoUri: z.string().optional(),
                })
              )
              .optional(),
          })
        )
        .optional(),
      reviews: z
        .array(
          z.object({
            name: z.string().optional().default(''),
            relativePublishTimeDescription: z.string().optional().default(''),
            rating: z.number().optional().default(0),
            text: z
              .object({
                text: z.string().optional().default(''),
                languageCode: z.string().optional().default('en'),
              })
              .optional()
              .nullable(),
            originalText: z
              .object({
                text: z.string().optional().default(''),
                languageCode: z.string().optional().default('en'),
              })
              .optional()
              .nullable(),
            authorAttribution: z
              .object({
                displayName: z.string().optional().default(''),
                uri: z.string().optional().default(''),
                photoUri: z.string().optional().default(''),
              })
              .optional()
              .nullable(),
            publishTime: z.string().optional().default(''),
          })
        )
        .optional()
        .default([]),
      types: z.array(z.string()).optional().default([]),
      nationalPhoneNumber: z.string().optional().default(''),
      internationalPhoneNumber: z.string().optional().default(''),
      businessStatus: z
        .enum(['OPERATIONAL', 'CLOSED_TEMPORARILY', 'CLOSED_PERMANENTLY'])
        .optional()
        .nullable(),
      servesBreakfast: z.boolean().optional().default(false),
      servesLunch: z.boolean().optional().default(false),
      servesDinner: z.boolean().optional().default(false),
      servesBeer: z.boolean().optional().default(false),
      servesWine: z.boolean().optional().default(false),
      servesVegetarianFood: z.boolean().optional().default(false),
      servesVeganFood: z.boolean().optional().default(false),
      takeout: z.boolean().optional().default(false),
      delivery: z.boolean().optional().default(false),
      dineIn: z.boolean().optional().default(false),
      reservable: z.boolean().optional().default(false),
      goodForChildren: z.boolean().optional().default(false),
      goodForGroups: z.boolean().optional().default(false),
      goodForWatchingSports: z.boolean().optional().default(false),
      paymentOptions: z
        .object({
          acceptsCashOnly: z.boolean().optional().default(false),
          acceptsCreditCards: z.boolean().optional().default(false),
          acceptsDebitCards: z.boolean().optional().default(false),
          acceptsNfc: z.boolean().optional().default(false),
        })
        .optional()
        .nullable(),
      parkingOptions: z
        .object({
          freeParkingLot: z.boolean().optional().default(false),
          paidParkingLot: z.boolean().optional().default(false),
          freeStreetParking: z.boolean().optional().default(false),
          paidStreetParking: z.boolean().optional().default(false),
          valetParking: z.boolean().optional().default(false),
        })
        .optional()
        .nullable(),
      accessibilityOptions: z
        .object({
          wheelchairAccessibleParking: z.boolean().optional().default(false),
          wheelchairAccessibleEntrance: z.boolean().optional().default(false),
          wheelchairAccessibleRestroom: z.boolean().optional().default(false),
          wheelchairAccessibleSeating: z.boolean().optional().default(false),
        })
        .optional()
        .nullable(),
      allowsDogs: z.boolean().optional().default(false),
      restroom: z.boolean().optional().default(false),
      outdoorSeating: z.boolean().optional().default(false),
      liveMusic: z.boolean().optional().default(false),
      menuForChildren: z.boolean().optional().default(false),
      servesCocktails: z.boolean().optional().default(false),
      servesDessert: z.boolean().optional().default(false),
      servesCoffee: z.boolean().optional().default(false),
      currentOpeningHours: z
        .object({
          openNow: z.boolean().optional().default(false),
          periods: z
            .array(
              z.object({
                open: z
                  .object({
                    day: z.number().optional().default(0),
                    hour: z.number().optional().default(0),
                    minute: z.number().optional().default(0),
                    date: z
                      .object({
                        year: z.number().optional().default(0),
                        month: z.number().optional().default(0),
                        day: z.number().optional().default(0),
                      })
                      .optional()
                      .nullable(),
                  })
                  .optional()
                  .nullable(),
                close: z
                  .object({
                    day: z.number().optional().default(0),
                    hour: z.number().optional().default(0),
                    minute: z.number().optional().default(0),
                    date: z
                      .object({
                        year: z.number().optional().default(0),
                        month: z.number().optional().default(0),
                        day: z.number().optional().default(0),
                      })
                      .optional()
                      .nullable(),
                  })
                  .optional()
                  .nullable(),
              })
            )
            .optional()
            .default([]),
          weekdayDescriptions: z.array(z.string()).optional().default([]),
        })
        .optional()
        .nullable(),
      primaryType: z.string().optional().default(''),
      shortFormattedAddress: z.string().optional().default(''),
      editorialSummary: z
        .object({
          text: z.string().optional().default(''),
          languageCode: z.string().optional().default('en'),
        })
        .optional()
        .nullable(),
      generativeSummary: z
        .object({
          overview: z
            .object({
              text: z.string().optional().default(''),
              languageCode: z.string().optional().default('en'),
            })
            .optional()
            .nullable(),
          overviewFlagContentUri: z.string().optional().default(''),
          disclosureText: z
            .object({
              text: z.string().optional().default(''),
              languageCode: z.string().optional().default('en'),
            })
            .optional()
            .nullable(),
        })
        .optional()
        .nullable(),
      reviewSummary: z
        .object({
          text: z
            .object({
              text: z.string().optional().default(''),
              languageCode: z.string().optional().default('en'),
            })
            .optional()
            .nullable(),
          flagContentUri: z.string().optional().default(''),
          disclosureText: z
            .object({
              text: z.string().optional().default(''),
              languageCode: z.string().optional().default('en'),
            })
            .optional()
            .nullable(),
          reviewsUri: z.string().optional().default(''),
        })
        .optional()
        .nullable(),
      plusCode: z
        .object({
          globalCode: z.string(),
          compoundCode: z.string().optional().default(''),
        })
        .optional()
        .nullable(),
      addressComponents: z
        .array(
          z.object({
            longText: z.string(),
            shortText: z.string(),
            types: z.array(z.string()),
            languageCode: z.string(),
          })
        )
        .optional()
        .default([]),
      adrFormatAddress: z.string().optional().default(''),
      utcOffsetMinutes: z.number().optional().nullable(),
      iconMaskBaseUri: z.string().optional().default(''),
      iconBackgroundColor: z.string().optional().default(''),
      displayNameLanguageCode: z.string().optional().default(''),
      primaryTypeDisplayName: z
        .object({
          text: z.string(),
          languageCode: z.string(),
        })
        .optional()
        .nullable(),
      currentSecondaryOpeningHours: z
        .array(
          z.object({
            openNow: z.boolean(),
            periods: z
              .array(
                z.object({
                  open: z.object({
                    day: z.number(),
                    hour: z.number(),
                    minute: z.number(),
                  }),
                  close: z
                    .object({
                      day: z.number(),
                      hour: z.number(),
                      minute: z.number(),
                    })
                    .optional(),
                })
              )
              .optional(),
            weekdayDescriptions: z.array(z.string()).optional(),
            secondaryHoursType: z.string(),
          })
        )
        .optional(),
      regularSecondaryOpeningHours: z
        .array(
          z.object({
            openNow: z.boolean().optional(),
            periods: z
              .array(
                z.object({
                  open: z.object({
                    day: z.number(),
                    hour: z.number(),
                    minute: z.number(),
                  }),
                  close: z
                    .object({
                      day: z.number(),
                      hour: z.number(),
                      minute: z.number(),
                    })
                    .optional(),
                })
              )
              .optional(),
            weekdayDescriptions: z.array(z.string()).optional(),
            secondaryHoursType: z.string(),
          })
        )
        .optional(),
    })
  ),
  nextPageToken: z.string().optional().default(''),
});

// Text Search Input Schema
const TextSearchInputSchema = z.object({
  textQuery: z.string(),
  locationBias: z
    .object({
      circle: z.object({
        center: z.object({
          latitude: z.number(),
          longitude: z.number(),
        }),
        radius: z.number(),
      }),
    })
    .optional()
    .nullable(),
  locationRestriction: z
    .object({
      rectangle: z.object({
        low: z.object({
          latitude: z.number(),
          longitude: z.number(),
        }),
        high: z.object({
          latitude: z.number(),
          longitude: z.number(),
        }),
      }),
    })
    .optional()
    .nullable(),
  includedType: z.string().optional().nullable(),
  pageSize: z.number().min(1).max(20).default(10),
  minRating: z.number().min(0).max(5).optional().nullable(),
  priceLevels: z
    .array(
      z.enum([
        'PRICE_LEVEL_FREE',
        'PRICE_LEVEL_INEXPENSIVE',
        'PRICE_LEVEL_MODERATE',
        'PRICE_LEVEL_EXPENSIVE',
        'PRICE_LEVEL_VERY_EXPENSIVE',
      ])
    )
    .optional()
    .nullable(),
  openNow: z.boolean().optional().nullable(),
  languageCode: z.string().default('en'),
  regionCode: z.string().optional().nullable(),
  rankPreference: z.enum(['RELEVANCE', 'DISTANCE']).optional().nullable(),
  searchAlongRouteParameters: z
    .object({
      polyline: z.object({
        encodedPolyline: z.string(),
      }),
    })
    .optional()
    .nullable(),
});

// Google Places API Result Schemas
const PlaceSchema = z.object({
  type: z.enum(['restaurant', 'activity', 'attraction']).default('attraction'),
  placeId: z.string(),
  name: z.string(),
  address: z.string(),
  location: CoordinatesSchema,
  rating: z.number().min(0).max(5).optional().nullable(),
  priceLevel: z
    .enum([
      'PRICE_LEVEL_FREE',
      'PRICE_LEVEL_INEXPENSIVE',
      'PRICE_LEVEL_MODERATE',
      'PRICE_LEVEL_EXPENSIVE',
      'PRICE_LEVEL_VERY_EXPENSIVE',
    ])
    .optional()
    .nullable(),
  types: z.array(z.string()),
  photos: z
    .array(
      z.object({
        photoReference: z.string(),
        width: z.number(),
        height: z.number(),
        url: z.string().optional().default(''),
      })
    )
    .optional()
    .default([]),
  openingHours: z
    .object({
      isOpen: z.boolean().optional().default(false),
      periods: z
        .array(
          z.object({
            open: z.object({
              day: z.number().min(0).max(6),
              time: z.string(),
            }),
            close: z
              .object({
                day: z.number().min(0).max(6),
                time: z.string(),
              })
              .optional()
              .nullable(),
          })
        )
        .optional()
        .default([]),
      weekdayText: z.array(z.string()).optional().default([]),
    })
    .optional()
    .nullable(),
  website: z.string().url().optional().default(''),
  phoneNumber: z.string().optional().default(''),
  reviews: z
    .array(
      z.object({
        authorName: z.string(),
        rating: z.number().min(1).max(5),
        text: z.string(),
        time: z.number(),
      })
    )
    .optional()
    .default([]),
  generativeSummary: z.string().optional().default(''),
  reviewSummary: z.string().optional().default(''),
  suggestedByAI: z.boolean().optional().default(false),
});

const RestaurantSchema = PlaceSchema.extend({
  type: z.literal('restaurant'),
  cuisine: z.array(z.string()).optional().default([]),
  servesVegetarian: z.boolean().optional().default(false),
  servesVegan: z.boolean().optional().default(false),
  reservable: z.boolean().optional().default(false),
  takeout: z.boolean().optional().default(false),
  delivery: z.boolean().optional().default(false),
});

const ActivitySchema = PlaceSchema.extend({
  type: z.literal('activity'),
  duration: z
    .object({
      min: z.number(), // minutes
      max: z.number().optional().nullable(),
    })
    .optional()
    .nullable(),
  category: z.string().default('tourist_attraction'),
  ticketPrice: PriceSchema.optional().nullable(),
  bookingRequired: z.boolean().optional().default(false),
  ageRestriction: z
    .object({
      min: z.number().optional().nullable(),
      max: z.number().optional().nullable(),
    })
    .optional()
    .nullable(),
});

export {
  GooglePlacesResponseSchema,
  TextSearchInputSchema,
  PlaceSchema,
  RestaurantSchema,
  ActivitySchema,
};

export type TextSearchInput = z.infer<typeof TextSearchInputSchema>;
export type GooglePlacesResponse = z.infer<typeof GooglePlacesResponseSchema>;
export type GooglePlace = GooglePlacesResponse['places'][0];

export type Place = z.infer<typeof PlaceSchema>;
export type Restaurant = z.infer<typeof RestaurantSchema>;
export type Activity = z.infer<typeof ActivitySchema>;
